import React, { useCallback, useMemo } from 'react'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Stack, Text } from '@ui'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { FancyButton } from 'ui/components/Button'
import {
    type Address,
    SpaceAddressFromSpaceId,
    useBotData,
    useRenewAppTransaction,
    useSpaceData,
} from 'use-towns-client'

import { useAppRenewCountdownData } from './hooks/useAppRenewCountdownData'
import { AppRenewCountdown } from './AppRenewCountdown'

export function RenewBotInfoAlert(props: { spaceId: string; appAddress: Address; cta?: boolean }) {
    const { spaceId, appAddress, cta } = props
    const { data: bot } = useBotData(appAddress)
    const {
        isExpired,
        timeRemaining,
        isLoading: isLoadingExpiration,
    } = useAppRenewCountdownData({
        spaceId,
        appAddress,
    })
    const space = useSpaceData()
    const { execute: renewApp, isLoading } = useRenewAppTransaction()

    const onRenewClick = useCallback(
        async (getSigner: GetSigner) => {
            const signer = await getSigner()
            if (!signer) {
                return createPrivyNotAuthenticatedNotification()
            }
            if (!bot?.installPrice) {
                return
            }
            renewApp({
                appAddress,
                spaceAddress: SpaceAddressFromSpaceId(spaceId) as `0x${string}`,
                signer,
                installPrice: bot.installPrice,
            })
        },
        [appAddress, bot?.installPrice, renewApp, spaceId],
    )

    const message = useMemo(() => {
        if (isExpired) {
            return 'Renew now to get access.'
        } else {
            if (space?.name) {
                return `Renew now to stay in ${space?.name}.`
            }
            return 'Renew now before it expires.'
        }
    }, [isExpired, space?.name])

    // Prevent flickering by waiting for data to load
    if (isLoadingExpiration) {
        return null
    }

    return (
        <Stack
            horizontal
            rounded="md"
            padding="md"
            gap="md"
            background="negativeSubtle"
            alignItems="center"
        >
            <AppRenewCountdown spaceId={spaceId} appAddress={appAddress} />
            <Stack grow gap="sm">
                <Text strong color="default" size="sm">
                    {isExpired
                        ? `${bot?.metadata?.displayName} membership has expired.`
                        : `${bot?.metadata?.displayName} membership expires in ${timeRemaining}.`}
                </Text>
                <Text color="gray2" size="sm">
                    {message}
                </Text>
            </Stack>
            {cta && (
                <WalletReady>
                    {({ getSigner }) => (
                        <FancyButton
                            compact
                            background="purpleGradient"
                            color="default"
                            borderRadius="full"
                            disabled={isLoading}
                            onClick={() => onRenewClick(getSigner)}
                        >
                            Renew
                        </FancyButton>
                    )}
                </WalletReady>
            )}
        </Stack>
    )
}
